import { Link } from "react-router-dom";
import { Heart, Plus, Timer } from "lucide-react";
import { useStore } from "../context/StoreContext";
import { inr, pctOff, buildLabel } from "../lib/api";
import { Stars } from "./Stars";

export const renderOf = (p, key) => {
  const v = p.variants?.find((x) => x.key === key) || p.variants?.[0];
  return v?.render || p.render || v?.images?.[0] || p.images?.[0];
};

export const ProductCard = ({ product: p, index = 0 }) => {
  const { addToCart, toggleWishlist, inWishlist } = useStore();
  const saved = inWishlist(p.slug);
  const lifestyle = p.images?.[0];
  return (
    <article data-testid={`product-card-${p.slug}`} className="group flex flex-col">
      <div className="render card-hover aspect-square">
        <Link to={`/product/${p.slug}`} data-testid={`product-card-link-${p.slug}`} className="block h-full w-full">
          <img src={renderOf(p)} alt={p.name} loading="lazy" className="h-full w-full object-cover transition-opacity duration-500 group-hover:opacity-0" />
          {lifestyle && <img src={lifestyle} alt="" loading="lazy" aria-hidden="true" className="absolute inset-0 h-full w-full scale-105 object-cover opacity-0 transition-[opacity,transform] duration-700 group-hover:scale-100 group-hover:opacity-100" />}
        </Link>
        <div className="absolute left-3 top-3 flex gap-1.5">
          {p.badge && <span className="rounded-full bg-walnut px-2.5 py-1 text-[11px] font-semibold text-linen">{p.badge}</span>}
          {pctOff(p) > 0 && <span className="rounded-full bg-sheesham px-2.5 py-1 text-[11px] font-semibold text-linen">{pctOff(p)}% off</span>}
        </div>
        <button
          data-testid={`wishlist-toggle-${p.slug}`}
          onClick={() => toggleWishlist(p)}
          aria-label={saved ? "Remove from wishlist" : "Save to wishlist"}
          className="absolute right-3 top-3 grid h-9 w-9 place-items-center rounded-full bg-linen/95 text-walnut shadow-sm backdrop-blur transition-transform hover:scale-110 active:scale-95"
        >
          <Heart className={`h-4 w-4 transition-colors ${saved ? "fill-sheesham text-sheesham" : ""}`} />
        </button>
        <button
          data-testid={`quick-add-${p.slug}`}
          onClick={() => addToCart(p)}
          className="product-quick-add absolute bottom-3 right-3 flex translate-y-2 items-center gap-1.5 rounded-lg bg-sheesham px-4 py-2.5 text-xs font-semibold text-linen opacity-0 shadow-sm transition-[opacity,transform,background-color] duration-300 hover:bg-[#743c22] group-hover:translate-y-0 group-hover:opacity-100 focus-visible:translate-y-0 focus-visible:opacity-100 max-md:translate-y-0 max-md:opacity-100"
        >
          <Plus className="h-3.5 w-3.5" /> Quick add
        </button>
      </div>
      <div className="mt-4 flex flex-1 flex-col gap-1.5 px-1">
        <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1"><Timer className="h-3.5 w-3.5" />{buildLabel(p.build_minutes)}</span>
          {p.review_count > 0 ? (
            <span className="inline-flex items-center gap-1"><Stars value={p.rating} size={12} />({p.review_count})</span>
          ) : p.variants?.length > 0 && (
            <span data-testid={`card-swatches-${p.slug}`} className="flex items-center gap-1.5">
              {p.variants.map((v) => (
                <Link key={v.key} to={`/product/${p.slug}?finish=${v.key}`} title={v.name} aria-label={v.name} style={{ backgroundColor: v.swatch }} className="h-3.5 w-3.5 rounded-full ring-1 ring-walnut/15 ring-offset-1 ring-offset-background" />
              ))}
              <span>{p.variants.length} finishes</span>
            </span>
          )}
        </div>
        <Link to={`/product/${p.slug}`} className="t-h4 transition-colors hover:text-sheesham">{p.name}</Link>
        <p className="line-clamp-2 text-sm text-muted-foreground">{p.tagline}</p>
        <div className="mt-auto flex flex-wrap items-baseline gap-x-2 gap-y-1 pt-1">
          <span data-testid={`product-price-${p.slug}`} className="price text-xl">{inr(p.price)}</span>
          {p.mrp > p.price && <span className="mrp text-sm">{inr(p.mrp)}</span>}
          {p.gst_exclusive && <span className="text-xs text-muted-foreground">excl. tax</span>}
        </div>
      </div>
    </article>
  );
};
